import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, Eye, UserCheck, FileText, Mail } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Alert, AlertDescription } from '../ui/alert';
import PageHeader from '../common/PageHeader';
import Footer from '../common/Footer';

const Privacy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <PageHeader />

      {/* Content */}
      <div className="container max-w-4xl py-16">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="h-10 w-10 text-primary" />
          <div>
            <h1 className="text-4xl font-bold">Integritet & personuppgifter</h1>
            <p className="text-muted-foreground mt-2">Uppdaterad: {new Date().toLocaleDateString('sv-SE')}</p>
          </div>
        </div>

        <Alert className="mb-8">
          <Lock className="h-4 w-4" />
          <AlertDescription className="text-base">
            Din integritet är viktig för oss. Här förklarar vi hur vi samlar in, använder och skyddar dina personuppgifter.
          </AlertDescription>
        </Alert>

        <div className="space-y-8">
          <section>
            <div className="flex items-center gap-3 mb-4">
              <FileText className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold">Personuppgifter vi samlar in</h2>
            </div>
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted-foreground mb-4">
                  Vi samlar endast in uppgifter som behövs för att leverera vår tjänst, till exempel:
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Namn</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>E-postadress</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Företagsuppgifter</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Order- och designrelaterad information</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </section>

          <Separator />

          <section>
            <div className="flex items-center gap-3 mb-4">
              <Eye className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold">Varför vi samlar in uppgifter</h2>
            </div>
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted-foreground mb-4">Vi använder personuppgifter för att:</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-2">
                    <Badge variant="outline">1</Badge>
                    <div>
                      <p className="font-medium mb-1">Tillhandahålla designverktyget</p>
                      <p className="text-sm text-muted-foreground">För att du ska kunna skapa och spara dina designer</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline">2</Badge>
                    <div>
                      <p className="font-medium mb-1">Hantera granskning och beställningar</p>
                      <p className="text-sm text-muted-foreground">För att producera och leverera dina dekaler</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline">3</Badge>
                    <div>
                      <p className="font-medium mb-1">Kommunicera med dig</p>
                      <p className="text-sm text-muted-foreground">För orderbekräftelser och support</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline">4</Badge>
                    <div>
                      <p className="font-medium mb-1">Uppfylla lagkrav</p>
                      <p className="text-sm text-muted-foreground">För bokföring och redovisning</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          <Separator />

          <section>
            <div className="flex items-center gap-3 mb-4">
              <Lock className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold">Lagring & skydd</h2>
            </div>
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted-foreground leading-relaxed">
                  Uppgifter lagras säkert och endast så länge det är nödvändigt. Vi delar aldrig personuppgifter 
                  med tredje part i marknadsföringssyfte.
                </p>
              </CardContent>
            </Card>
          </section>

          <Separator />

          <section>
            <div className="flex items-center gap-3 mb-4">
              <UserCheck className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold">Dina rättigheter</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Begära utdrag</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Se vilka uppgifter vi har om dig
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Få uppgifter rättade</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Korrigera felaktig information
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Begära radering</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Ta bort dina personuppgifter
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Invända mot behandling</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Motsätta dig viss databehandling
                  </p>
                </CardContent>
              </Card>
            </div>
            <Alert className="mt-4">
              <Mail className="h-4 w-4" />
              <AlertDescription>
                Kontakta oss på <a href="mailto:info@dekalio.nu" className="underline">info@dekalio.nu</a> om du vill använda någon av dessa rättigheter.
              </AlertDescription>
            </Alert>
          </section>

          <div className="flex gap-4 pt-8">
            <Button onClick={() => navigate('/')}>
              Tillbaka till startsidan
            </Button>
            <Button variant="outline" onClick={() => navigate('/villkor')}>
              Läs våra villkor
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Privacy;

